import { useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../lib/api'
import { setToken, setCachedUser } from '../lib/auth'
import { useI18n, segments } from '../lib/i18n'
import type { User } from '../lib/types'

interface Props {
  onLogin: (user: User) => void
}

export default function Login({ onLogin }: Props) {
  const { t } = useI18n()
  const [step, setStep] = useState<'email' | 'code'>('email')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [inviteCode, setInviteCode] = useState(() => sessionStorage.getItem('fa_pending_invite') || '')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')
  const [err, setErr] = useState('')

  // "API 503: {json}" → {status, human message}; status 0 = network-level failure
  const parseApiError = (e: unknown): { status: number; message: string } => {
    if (e instanceof Error) {
      const m = e.message.match(/^API (\d+): ([\s\S]*)$/)
      if (m) {
        let msg = m[2]
        try { msg = (JSON.parse(m[2]) as { error?: string }).error || msg } catch { /* not JSON */ }
        return { status: Number(m[1]), message: msg }
      }
    }
    return { status: 0, message: '' }
  }

  const sendCode = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true); setErr('')
    try {
      const res = await api.login(email.trim())
      setMsg(res.message)
      setStep('code')
    } catch (e1) {
      const p1 = parseApiError(e1)
      // network blips and deploy-propagation 405/5xx are transient — retry once, quietly
      if (p1.status === 0 || p1.status === 405 || p1.status >= 500) {
        await new Promise(r => setTimeout(r, 1500))
        try {
          const res = await api.login(email.trim())
          setMsg(res.message)
          setStep('code')
          setBusy(false)
          return
        } catch (e2) {
          const p2 = parseApiError(e2)
          setErr(p2.message || t('login.errUnreachable'))
          setBusy(false)
          return
        }
      }
      setErr(p1.message || t('login.errSend'))
    } finally { setBusy(false) }
  }

  const verify = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true); setErr('')
    try {
      const res = await api.verify(email.trim(), code.trim(), inviteCode.trim() || undefined)
      setToken(res.token)
      setCachedUser(res.user)
      onLogin(res.user)
      // resume a pending circle join (arrived via /join/:token while logged out)
      const pending = sessionStorage.getItem('fa_pending_join')
      if (pending) {
        sessionStorage.removeItem('fa_pending_join')
        window.location.href = `/join/${pending}`
      }
    } catch (e1) {
      const p = parseApiError(e1)
      setErr(p.status === 429 || p.status >= 500 ? (p.message || t('login.errRetry')) : t('login.errInvalid'))
    } finally { setBusy(false) }
  }

  return (
    <div className="min-h-screen bg-void-950 flex items-center justify-center px-4 relative overflow-hidden">
      <div className="aurora aurora-gold aurora-drift w-[400px] h-[400px] -top-20 -left-20" />
      <div className="aurora aurora-violet aurora-drift-2 w-[380px] h-[380px] -bottom-24 -right-16" />

      <div className="card-glass p-8 w-full max-w-md relative z-10 fade-up">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <span className="text-2xl">🌳</span>
          <span className="font-display font-semibold text-lg text-gradient-gold">Family Ancestry</span>
        </Link>

        {step === 'email' ? (
          <form onSubmit={sendCode}>
            {sessionStorage.getItem('fa_pending_invite') && (
              <p className="text-xs text-gold-300/90 mb-3">✨ {t('login.invited')}</p>
            )}
            <h1 className="font-display text-2xl font-bold text-slate-100 mb-2">{t('login.title')}</h1>
            <p className="text-sm text-slate-400 mb-6">{t('login.sub')}</p>
            <input
              type="email" required autoFocus placeholder={t('login.emailPlaceholder')}
              value={email} onChange={e => setEmail(e.target.value)}
              className="input-dark mb-4"
            />
            <button type="submit" disabled={busy || !email.includes('@')} className="btn-gold w-full">
              {busy ? t('login.sending') : t('login.sendCode')}
            </button>
            <p className="text-[11px] text-slate-500 mt-4 text-center">
              {segments(t('login.terms')).map((seg, i) =>
                seg.tag === 'terms' ? <Link key={i} to="/terms" className="text-gold-400/80 hover:text-gold-300">{seg.text}</Link>
                : seg.tag === 'privacy' ? <Link key={i} to="/privacy" className="text-gold-400/80 hover:text-gold-300">{seg.text}</Link>
                : <span key={i}>{seg.text}</span>)}
            </p>
          </form>
        ) : (
          <form onSubmit={verify}>
            <h1 className="font-display text-2xl font-bold text-slate-100 mb-2">{t('login.checkTitle')}</h1>
            <p className="text-sm text-slate-400 mb-6">
              {msg || segments(t('login.sentTo', { email })).map((seg, i) =>
                seg.tag === 'email' ? <span key={i} dir="ltr" className="text-slate-200">{seg.text}</span> : <span key={i}>{seg.text}</span>)}
            </p>
            <input
              type="text" required autoFocus placeholder={t('login.codePlaceholder')} inputMode="numeric" maxLength={6}
              value={code} onChange={e => setCode(e.target.value)}
              className="input-dark mb-3 text-center text-2xl tracking-[0.5em] font-mono"
            />
            <input
              type="text" placeholder={t('login.invitePlaceholder')}
              value={inviteCode} onChange={e => setInviteCode(e.target.value)}
              className="input-dark mb-4"
            />
            <button type="submit" disabled={busy || code.length < 6} className="btn-gold w-full mb-3">
              {busy ? t('login.verifying') : t('login.open')}
            </button>
            <button type="button" onClick={() => { setStep('email'); setCode('') }}
              className="w-full text-sm text-slate-500 hover:text-slate-300">
              {t('login.otherEmail')}
            </button>
          </form>
        )}

        {err && <p className="mt-4 text-sm text-red-400">{err}</p>}
        <p className="mt-6 text-xs text-slate-600">
          {t('login.newHere')}
        </p>
      </div>
    </div>
  )
}
